import { useEffect, useState, useRef } from "react";
import cx from "classnames";

import { useAuthentication } from "common/auth/hooks";
import { SpecialtyDesignations } from "common/types/clinics";
import { Feature } from "common/types";

import Container from "client/common/components/layouts/Container";
import BrowseSupport from "client/products/components/BrowseSupport";
import { useClinic, useFeatures } from "client/clinics/hooks";
import PreFooter from "client/products/components/PreFooter";
import Header from "client/common/components/header/Header";

import LoggedInWelcomeSummary from "./LoggedInWelcomeSummary";
import HomeBrowseV1 from "./HomeBrowseV1";
import HomeBrowseV2 from "./HomeBrowseV2";

import styles from "../styles/HomeContent.module.less";

const normalizeColors = (colors: string | string[]) => {
  if (typeof colors === "string") {
    return [colors, colors, colors];
  }
  return colors;
};

export type LineColors = string | [string, string, string];

type LineProps = {
  colors?: LineColors;
};

const Line = ({ colors = "#FFFFFF" }: LineProps) => {
  const [pathLength, setPathLength] = useState(1000000); // Default to something high so it's invisible
  const pathRef = useRef<SVGPathElement>(null);

  const [cA, cB, cC] = normalizeColors(colors);

  // Kick off the animation!
  useEffect(() => {
    setPathLength((l) => pathRef.current?.getTotalLength() || l);
  }, []);

  return (
    <svg
      width="406"
      viewBox="0 0 406 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.line}
    >
      <path
        d="M2 12C101.596 2.16077 209.096 0.37924 310.596 3.22986C337.339 4.28868 390.867 6.3718 391.883 6.41137C391.915 6.41261 391.914 6.41184 391.946 6.41218C392.274 6.41562 394.478 6.46419 404 7.11045"
        stroke="url(#underline-fill)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={pathLength}
        strokeDashoffset={pathLength}
        ref={pathRef}
        className={styles.path}
      />
      <defs>
        <linearGradient
          id="underline-fill"
          x1="-1.57161"
          y1="2.0002"
          x2="401"
          y2="5.03531"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={cA}>
            <animate
              attributeName="stop-color"
              values={[cA, cC, cB, cA].join("; ")}
              dur="4s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="0.5" stopColor={cB}>
            <animate
              attributeName="stop-color"
              values={[cB, cA, cC, cB].join("; ")}
              dur="4s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="1" stopColor={cC}>
            <animate
              attributeName="stop-color"
              values={[cC, cB, cA, cC].join("; ")}
              dur="4s"
              repeatCount="indefinite"
            />
          </stop>
        </linearGradient>
      </defs>
    </svg>
  );
};

/**
 * Because we pixel push the title and its underline to look perfect
 * at any viewport size, use a centralized component to render this.
 */
const HomeTitle = ({ lineColors, className }: { lineColors?: LineColors; className?: string }) => {
  const clinic = useClinic();
  return (
    <div className={cx(styles.homeTitleContainer, className)}>
      <span className={styles.homeTitleContainerFirstLine}>
        Fill your{" "}
        <span className={styles.homeTitleUnderline}>
          {clinic.clinic_metadata?.specialty_designation === SpecialtyDesignations.EquineOnly
            ? "animal"
            : "pet"}
          &apos;s medications <Line colors={lineColors} />
        </span>
      </span>
      <span className={styles.homeTitleContainerSecondLine}>from the comfort of your home</span>
    </div>
  );
};

type HomeHeaderProps = {
  children: React.ReactNode;
};

const HomeHeader = ({ children }: HomeHeaderProps) => {
  return (
    <Container>
      <div className={styles.searchbar}>
        <Header.SearchLink />
      </div>
      {children}
    </Container>
  );
};

type HomeBodyProps = {
  hasLoggedinSummary: boolean;
  hasRoundedTop?: boolean;
};

const HomeBody = ({ hasLoggedinSummary, hasRoundedTop = true }: HomeBodyProps) => {
  const clinic = useClinic();
  const { isAuthenticated } = useAuthentication();
  const features = useFeatures();
  const HomeBrowse = features[Feature.CarouselV2] ? HomeBrowseV2 : HomeBrowseV1;
  return (
    <>
      <div className={cx(styles.main, hasRoundedTop && styles.roundedTop)}>
        <Container>
          <div className={styles.browseContainer}>
            {isAuthenticated && hasLoggedinSummary && <LoggedInWelcomeSummary />}
            <HomeBrowse clinic={clinic} />
          </div>
        </Container>
      </div>
      <div className={styles.support}>
        <Container>
          <BrowseSupport />
        </Container>
      </div>
      <PreFooter image={clinic.storefront_prefooter_image} />
    </>
  );
};

/**
 * For each home theme component (the content of the HomePage), we'd expect a pattern similar
 * to this, with additional content and custom props added in:
 *
 * <>
 *   <Header />
 *   <HomeContent.Header>
 *     <HomeContent.Title />
 *   </HomeContent.Header>
 *   <HomeContent.Body />
 * </>
 */
export default {
  Title: HomeTitle,
  Header: HomeHeader,
  Body: HomeBody,
};
